/**
 * One shop order, as an operator needs to see it.
 *
 * The old page was a stack of five full-width cards titled with a cuid, and it
 * treated an order as a flat list of facts. It is not: it is a basket that fans
 * out into one shipment per vendor, and every question worth asking about it is
 * a question about a shipment — who has to pack this, has it moved, can the
 * customer see where it is.
 *
 * So the shipments lead, each as its own panel with its own status, tracking and
 * history, and the order-level facts (who, where, what was paid) sit beside them
 * rather than above them.
 */
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  Boxes,
  Copy,
  CreditCard,
  FileText,
  History,
  IndianRupee,
  MapPin,
  Package,
  PillBottle,
  Store,
  Truck,
  User,
} from 'lucide-react';
import { DashboardLayout } from '@/components/dashboard-layout';
import {
  DetailPanel,
  Field,
  FieldList,
  PanelEmpty,
  PanelSkeleton,
} from '@/components/detail/detail-panel';
import { MetricStrip, type Metric } from '@/components/metric-strip';
import { PersonAvatar } from '@/components/person-avatar';
import { AuthImage, open_file_handler } from '@/components/auth-image';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { notify } from '@/components/ui/toaster';
import { ago, date_time, duration_since, inr, short_id, title_case } from '@/lib/format';
import { use_commerce_order_detail } from '@/features/commerce-orders/hooks';
import {
  COMMERCE_STATUS_LABELS,
  OPEN_COMMERCE_STATUSES,
  type CommerceOrder,
  type CommerceShipment,
} from '@/features/commerce-orders/types';
import { COMMERCE_STATUS_TONE } from './commerce-order-list-page';

const PAYMENT_TONE: Record<string, 'success' | 'warning' | 'neutral' | 'destructive'> = {
  CAPTURED: 'success',
  PENDING: 'warning',
  REFUNDED: 'destructive',
  FAILED: 'destructive',
};

/** Who moved the shipment, said as a role rather than a table name. */
const ACTOR_LABELS: Record<string, string> = {
  USER: 'Customer',
  VENDOR: 'Vendor',
  ADMIN: 'Admin',
  SYSTEM: 'Automatic',
};

/** Moving, with nothing the customer could track it by. */
function is_untracked(s: CommerceShipment): boolean {
  return (s.status === 'ACCEPTED' || s.status === 'OUT_FOR_DELIVERY') && !s.tracking_ref;
}

function what_they_bought(o: CommerceOrder): string {
  const lines = o.shipments.flatMap((s) => s.lines);
  const units = lines.reduce((n, l) => n + l.quantity, 0);
  const vendors = new Set(o.shipments.map((s) => s.vendor_profile_id)).size;
  if (lines.length === 0) return 'An empty basket';
  return [
    `${units} ${units === 1 ? 'unit' : 'units'} across ${lines.length} ${lines.length === 1 ? 'line' : 'lines'}`,
    `${vendors} ${vendors === 1 ? 'vendor' : 'vendors'}`,
    `placed ${ago(o.created_at)}`,
  ].join(' · ');
}

export function CommerceOrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: o, isLoading, error } = use_commerce_order_detail(id);
  const [file_error, set_file_error] = useState<string | null>(null);

  const open_shipments =
    o?.shipments.filter((s) => OPEN_COMMERCE_STATUSES.includes(s.status)) ?? [];
  const untracked = o?.shipments.filter(is_untracked) ?? [];

  const metrics: Metric[] = [
    {
      key: 'total',
      label: 'Order total',
      value: o ? inr(o.grand_total_inr) : undefined,
      hint:
        o && Number(o.delivery_fee_inr) > 0
          ? `incl. ${inr(o.delivery_fee_inr)} delivery`
          : undefined,
      icon: IndianRupee,
      loading: !o,
    },
    {
      key: 'payment',
      label: 'Payment',
      value: o?.payment ? title_case(o.payment.status) : 'None',
      hint: o?.payment ? inr(o.payment.amount_inr) : 'Nothing taken',
      tone: o?.payment?.status === 'CAPTURED' ? 'default' : o?.payment ? 'warning' : 'default',
      icon: CreditCard,
      loading: !o,
    },
    {
      key: 'open',
      label: 'Shipments open',
      value: o ? `${open_shipments.length} of ${o.shipments.length}` : undefined,
      hint: open_shipments.length === 0 ? 'All settled' : 'Still somebody else’s job',
      tone: open_shipments.length > 0 ? 'warning' : 'default',
      icon: Boxes,
      loading: !o,
    },
    {
      key: 'tracking',
      label: 'Untracked parcels',
      value: o ? untracked.length.toLocaleString('en-IN') : undefined,
      hint: untracked.length > 0 ? 'Customer cannot see these' : 'Nothing missing',
      tone: untracked.length > 0 ? 'destructive' : 'default',
      icon: Truck,
      loading: !o,
    },
  ];

  return (
    <DashboardLayout
      title={o?.customer_name ?? o?.customer_phone ?? 'Shop order'}
      subtitle={o ? what_they_bought(o) : undefined}
      breadcrumbs={[{ label: o ? short_id(o.id) : 'Order' }]}
      avatar={
        o ? <PersonAvatar path={null} name={o.customer_name} seed={o.user_id} size="lg" /> : undefined
      }
      title_badges={
        o ? (
          <>
            <Badge tone={COMMERCE_STATUS_TONE[o.status]} dot data-testid="order-status">
              {COMMERCE_STATUS_LABELS[o.status]}
            </Badge>
            <Badge tone="neutral">
              {o.vertical === 'PHARMACY'
                ? 'Medicines'
                : o.vertical === 'FOOD'
                  ? 'Food & products'
                  : 'Mixed basket'}
            </Badge>
            {o.prescription_required && (
              <Badge tone="warning" dot data-testid="badge-prescription">
                <PillBottle className="h-3 w-3" /> Prescription
              </Badge>
            )}
          </>
        ) : undefined
      }
      meta={
        o ? (
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="inline-flex items-center gap-1">
              <User aria-hidden="true" className="h-3.5 w-3.5" />
              {o.contact_phone}
            </span>
            {o.city && (
              <span className="inline-flex items-center gap-1">
                <MapPin aria-hidden="true" className="h-3.5 w-3.5" />
                {o.city}
                {o.pincode ? ` ${o.pincode}` : ''}
              </span>
            )}
            <button
              type="button"
              onClick={() => {
                void navigator.clipboard.writeText(o.id);
                notify.success('Order ID copied');
              }}
              className="inline-flex items-center gap-1 underline-offset-2 hover:text-foreground hover:underline"
              data-testid="copy-order-id"
            >
              <Copy aria-hidden="true" className="h-3.5 w-3.5" />
              {short_id(o.id)}
            </button>
          </span>
        ) : undefined
      }
      actions={
        o ? (
          <>
            <Button variant="outline" size="sm" asChild>
              <Link to={`/users/${o.user_id}`} data-testid="open-customer">
                <User className="h-4 w-4" /> Customer
              </Link>
            </Button>
            <Button variant="outline" size="sm" asChild>
              <Link
                to={`/commerce-orders?segment=all&user_id=${o.user_id}`}
                data-testid="their-other-orders"
              >
                <Package className="h-4 w-4" /> Their orders
              </Link>
            </Button>
          </>
        ) : undefined
      }
      stats={<MetricStrip metrics={metrics} />}
    >
      {error ? (
        <PanelEmpty icon={Package}>This order isn’t available. It may have been removed.</PanelEmpty>
      ) : isLoading || !o ? (
        <PanelSkeleton rows={6} />
      ) : (
        <div className="flex flex-col gap-gutter" data-testid="commerce-order-detail">
          {/*
            The two things that make an order somebody's problem, said before any
            of the detail: a parcel the customer can't see, and a cancellation
            whose reason is recorded nowhere else.
          */}
          {untracked.length > 0 && (
            <div
              className="flex items-start gap-2 rounded-panel bg-destructive/10 px-3 py-2.5 text-data text-destructive"
              data-testid="untracked-banner"
            >
              <AlertTriangle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                <strong className="font-semibold">
                  {untracked.length} {untracked.length === 1 ? 'parcel is' : 'parcels are'} moving
                  without a tracking reference
                </strong>{' '}
                — the customer has no way to see where{' '}
                {untracked.length === 1 ? 'it is' : 'they are'}. Ask the vendor to add one.
              </span>
            </div>
          )}

          {o.cancel_reason && (
            <div
              className="flex items-start gap-2 rounded-panel bg-muted px-3 py-2.5 text-data text-muted-foreground"
              data-testid="cancelled-banner"
            >
              <History aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                <strong className="font-semibold text-foreground">Cancelled</strong>
                {o.cancelled_by
                  ? ` by the ${(ACTOR_LABELS[o.cancelled_by] ?? o.cancelled_by).toLowerCase()}`
                  : ''}
                : “{o.cancel_reason}”
              </span>
            </div>
          )}

          {/* The shipments — the actual work — then the order-level facts. */}
          {o.shipments.length === 0 ? (
            <DetailPanel title="Shipments" icon={Boxes} test_id="commerce-order-shipments">
              <PanelEmpty icon={Boxes}>
                Nothing was split out to a vendor. An unpaid basket looks like this.
              </PanelEmpty>
            </DetailPanel>
          ) : (
            o.shipments.map((s, i) => (
              <ShipmentPanel key={s.id} shipment={s} index={i} count={o.shipments.length} />
            ))
          )}

          <div className="grid items-start gap-gutter lg:grid-cols-2">
            <DetailPanel title="Where it goes" icon={MapPin} test_id="commerce-order-address">
              <FieldList>
                <Field
                  label="Customer"
                  value={
                    <Link
                      to={`/users/${o.user_id}`}
                      className="text-primary underline-offset-2 hover:underline"
                    >
                      {o.customer_name ?? 'No name on file'}
                    </Link>
                  }
                />
                <Field label="Phone on the order" value={o.contact_phone} mono />
                <Field
                  label="Account phone"
                  value={
                    /*
                      Worth showing only when it differs — an order placed for
                      somebody else is exactly the case an operator chases.
                    */
                    o.customer_phone && o.customer_phone !== o.contact_phone
                      ? `${o.customer_phone} (differs)`
                      : null
                  }
                  empty="Same as the order"
                  mono
                />
                <Field label="Address" value={o.address} empty="No address given" />
                <Field label="Landmark" value={o.landmark} empty="None" />
                <Field
                  label="City"
                  value={[o.city, o.pincode].filter(Boolean).join(' ') || null}
                  empty="Not set"
                />
                <Field label="Note from the customer" value={o.notes} empty="None" />
              </FieldList>
            </DetailPanel>

            <DetailPanel title="Money" icon={IndianRupee} test_id="commerce-order-payment">
              <FieldList>
                <Field label="Goods" value={inr(o.goods_total_inr)} mono />
                <Field label="Delivery" value={inr(o.delivery_fee_inr)} mono />
                <Field
                  label="Charged"
                  mono
                  value={<span className="font-semibold">{inr(o.grand_total_inr)}</span>}
                />
                <Field
                  label="Payment"
                  value={
                    o.payment ? (
                      <span className="flex flex-wrap items-center gap-2">
                        <Badge tone={PAYMENT_TONE[o.payment.status] ?? 'neutral'} size="sm" dot>
                          {title_case(o.payment.status)}
                        </Badge>
                        <span className="tabular">{inr(o.payment.amount_inr)}</span>
                        {o.payment.is_dummy && (
                          <Badge tone="warning" size="sm">
                            test payment
                          </Badge>
                        )}
                      </span>
                    ) : null
                  }
                  empty="Nothing recorded"
                />
                <Field
                  label="Gateway reference"
                  value={o.payment?.order_ref ?? null}
                  empty="None"
                  mono
                />
                <Field label="Placed" value={date_time(o.created_at)} />
                <Field
                  label="Last moved"
                  /*
                    `duration_since` rather than `ago`: past a week `ago` gives
                    up and returns the date, which printed it twice.
                  */
                  value={`${date_time(o.updated_at)} · ${duration_since(o.updated_at)} ago`}
                />
              </FieldList>
            </DetailPanel>
          </div>

          {/*
            Prescriptions are the one thing on this page an admin must actually
            look at rather than read, so they get room instead of 64px thumbs.
          */}
          {(o.prescription_required || o.prescriptions.length > 0) && (
            <DetailPanel
              title="Prescriptions"
              icon={FileText}
              description={
                o.prescriptions.length === 0
                  ? 'This order needs one, and nothing has been uploaded.'
                  : 'Click a page to open it full size.'
              }
              test_id="commerce-order-prescriptions"
            >
              {o.prescriptions.length === 0 ? (
                <PanelEmpty icon={PillBottle}>
                  A prescription line is in this basket but no document was attached. It cannot be
                  dispensed until the customer uploads one.
                </PanelEmpty>
              ) : (
                <>
                  <div className="flex flex-wrap gap-2" data-testid="prescription-documents">
                    {o.prescriptions.map((doc) => (
                      <button
                        key={doc.id}
                        type="button"
                        onClick={open_file_handler(doc.full_path, set_file_error)}
                        className="h-32 w-28 overflow-hidden rounded-panel border border-border transition-shadow hover:shadow-elevated"
                        data-testid={`prescription-${doc.id}`}
                      >
                        <AuthImage
                          path={doc.thumb_path}
                          alt="Prescription"
                          className="h-full w-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                  {file_error && (
                    <p className="mt-2 text-data text-destructive" data-testid="prescription-error">
                      {file_error}
                    </p>
                  )}
                </>
              )}
            </DetailPanel>
          )}
        </div>
      )}
    </DashboardLayout>
  );
}

/**
 * One vendor's half of the order.
 *
 * Its own status, its own tracking, its own history — because a customer ringing
 * up about "my order" is really asking about one of these, and the order's
 * rollup status cannot answer.
 */
function ShipmentPanel({
  shipment: s,
  index,
  count,
}: {
  shipment: CommerceShipment;
  index: number;
  count: number;
}) {
  const units = s.lines.reduce((n, l) => n + l.quantity, 0);
  const audits = s.audits ?? [];

  return (
    <DetailPanel
      title={s.vendor_business_name ?? 'Vendor removed'}
      icon={Store}
      description={
        count > 1 ? `Shipment ${index + 1} of ${count} · ${units} units` : `${units} units`
      }
      actions={
        <div className="flex items-center gap-2">
          {is_untracked(s) && (
            <Badge tone="warning" size="sm" dot data-testid={`untracked-${s.id}`}>
              no tracking
            </Badge>
          )}
          <Badge tone={COMMERCE_STATUS_TONE[s.status]} dot data-testid={`shipment-status-${s.id}`}>
            {COMMERCE_STATUS_LABELS[s.status]}
          </Badge>
          <Button variant="outline" size="sm" asChild>
            <Link to={`/vendors/${s.vendor_profile_id}`} data-testid={`open-vendor-${s.id}`}>
              Open vendor
            </Link>
          </Button>
        </div>
      }
      test_id={`commerce-order-shipment-${s.id}`}
    >
      <div className="flex flex-col gap-4">
        {/* The lines, priced. The reason the order exists. */}
        <div className="overflow-x-auto">
          <table className="w-full text-data">
            <caption className="sr-only">Items in this shipment</caption>
            <thead>
              <tr className="border-b border-border text-left text-micro font-semibold uppercase tracking-wide text-muted-foreground">
                <th scope="col" className="py-1.5 pr-3">
                  Item
                </th>
                <th scope="col" className="py-1.5 pr-3 text-right">
                  Qty
                </th>
                <th scope="col" className="py-1.5 text-right">
                  Line total
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {s.lines.map((line) => (
                <tr key={line.id} data-testid={`commerce-shipment-line-${line.id}`}>
                  <td className="py-2 pr-3">
                    <span className="font-medium">{line.name}</span>
                    {line.variant_label && (
                      <span className="ml-1.5 text-muted-foreground">{line.variant_label}</span>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-right tabular text-muted-foreground">
                    {line.quantity}
                  </td>
                  <td className="py-2 text-right tabular">{inr(line.line_total_inr)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-border">
                <td className="py-2 pr-3 font-semibold" colSpan={2}>
                  Shipment subtotal
                </td>
                <td className="py-2 text-right tabular font-semibold">{inr(s.subtotal_inr)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <FieldList>
          <Field
            label="Tracking"
            mono
            value={
              s.tracking_url ? (
                <a
                  href={s.tracking_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary underline-offset-2 hover:underline"
                >
                  {s.tracking_ref ?? s.tracking_url}
                </a>
              ) : (
                s.tracking_ref
              )
            }
            empty={
              is_untracked(s)
                ? 'Nothing — the customer cannot follow this parcel'
                : 'Not needed yet'
            }
          />
        </FieldList>

        {/* What happened to it, as a trail rather than a table of enum names. */}
        <div>
          <h4 className="mb-2 flex items-center gap-1.5 text-micro font-semibold uppercase tracking-wide text-muted-foreground">
            <History aria-hidden="true" className="h-3.5 w-3.5" /> History
          </h4>
          {audits.length === 0 ? (
            <p className="text-data text-muted-foreground">
              Nothing recorded — it has not moved since it was created.
            </p>
          ) : (
            <ol className="flex flex-col gap-2">
              {audits.map((a) => (
                <li
                  key={a.id}
                  className="flex flex-wrap items-center gap-2 text-data"
                  data-testid={`shipment-audit-${a.id}`}
                >
                  <Badge tone={COMMERCE_STATUS_TONE[a.to_status]} size="sm" dot>
                    {COMMERCE_STATUS_LABELS[a.to_status]}
                  </Badge>
                  <span className="text-muted-foreground">
                    {a.from_status ? `from ${COMMERCE_STATUS_LABELS[a.from_status]} · ` : ''}
                    {date_time(a.created_at)} · {ACTOR_LABELS[a.actor_type] ?? a.actor_type}
                    {a.reason ? ` · “${a.reason}”` : ''}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    </DetailPanel>
  );
}

export default CommerceOrderDetailPage;
